import { useState } from "react";
import Column from "@amzn/meridian/esm/components/column";
import Row from "@amzn/meridian/esm/components/row";
import Input from "@amzn/meridian/esm/components/input";
import Textarea from "@amzn/meridian/esm/components/textarea";
import Icon from "@amzn/meridian/esm/components/icon";
import Tile from "@amzn/meridian/esm/components/tile";
import Theme from "@amzn/meridian/esm/components/theme";
import checkLargeTokens from "@amzn/meridian-tokens/base/icon/check-large";
import undoTokens from "@amzn/meridian-tokens/base/icon/undo";
import userTokens from "@amzn/meridian-tokens/base/icon/user";
import calendarTokens from "@amzn/meridian-tokens/base/icon/calendar";
import { ACCENT_COLOR, contentInputTheme, actionRowLayout, CONTENT_PLACEHOLDER, OWNER_PLACEHOLDER, DEADLINE_PLACEHOLDER } from "./actionItems.constants";
import IconButton from "../IconButton";

/**
 * ActionItemEditCard - Inline edit/add form for an action item. Used both to edit an
 * existing item (pass `item`) and to create a new one (omit `item`).
 *
 * Fields: content (required), owner, deadline. Save (check) commits via onSave;
 * cancel (undo) discards via onCancel. Enter in any field also saves.
 */
// Meridian's icon SVGs ship at a fixed native size and ignore icon size tokens, so the whole icon
// is scaled down. Local to this file: the two field icons beside the owner/deadline inputs are the
// only users (the card action buttons scale inside the shared IconButton instead).
const fieldIconStyle = { transform: "scale(0.65)" };

const ActionItemEditCard = ({ item, onSave, onCancel, onContentFocus }) => {
  const [content, setContent] = useState(item?.content || "");
  const [ownerName, setOwnerName] = useState(item?.ownerName || "");
  const [deadline, setDeadline] = useState(item?.deadline || "");

  const save = () => {
    // Empty content closes without saving rather than blocking the click. onCancel is the close
    // path — this component doesn't own its open state.
    if (!content.trim()) {
      onCancel();
      return;
    }
    onSave({
      content: content.trim(),
      ownerName: ownerName.trim() || null,
      deadline: deadline.trim() || null,
    });
  };

  // Enter saves from any text field (input/textarea) — content, owner, or deadline — so every
  // field commits consistently. The handler is on the wrapping div but guards on the target tag
  // so it only fires for text fields; buttons (Cancel/Save) keep their native Enter behavior.
  // Shift+Enter is left untouched so it still inserts a newline in the content textarea and never
  // saves; Esc cancels from any field.
  const onFieldKeyDown = (e) => {
    if (
      e.key === "Enter" &&
      !e.shiftKey &&
      (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA")
    ) {
      e.preventDefault();
      save();
    } else if (e.key === "Escape") {
      onCancel();
    }
  };

  return (
    <div onKeyDown={onFieldKeyDown}>
      <Tile accent={ACCENT_COLOR} spacingInset="300 350">
      <Column spacing="300">
        <Row {...actionRowLayout}>
          <Theme tokens={contentInputTheme}>
            <Textarea
              size="small"
              value={content}
              onChange={setContent}
              onFocus={onContentFocus}
              placeholder={CONTENT_PLACEHOLDER}
              resize="auto"
              rows={1}
              inaccessibleAutoFocus
            />
          </Theme>
          <IconButton
            icon={checkLargeTokens}
            onClick={save}
            ariaLabel="Save action item"
          />
          <IconButton
            icon={undoTokens}
            onClick={onCancel}
            ariaLabel="Cancel edit"
          />
        </Row>
        <Row spacing="200" alignmentVertical="center" widths={["fit", "fill", "fit", "fill"]}>
          <div style={fieldIconStyle}>
            <Icon tokens={userTokens} color="secondary" />
          </div>
          <Input
            size="small"
            value={ownerName}
            onChange={setOwnerName}
            placeholder={OWNER_PLACEHOLDER}
          />
          <div style={fieldIconStyle}>
            <Icon tokens={calendarTokens} color="secondary" />
          </div>
          <Input
            size="small"
            value={deadline}
            onChange={setDeadline}
            placeholder={DEADLINE_PLACEHOLDER}
          />
        </Row>
      </Column>
      </Tile>
    </div>
  );
};

export default ActionItemEditCard;
